import { api } from './client'

/**
 * F-065 (TASK-0319) — cockpit Direzione (`ExecutiveDashboardController`, `/api/reports/executive`, policy
 * Manager). NESSUN importo in nessun payload: né fatturato, provvigioni, crediti, margini o previsioni.
 * Ogni cella `{ count, drill }` è cliccabile: `drill` è la coppia da passare a `drilldown` con gli stessi filtri;
 * `count` è `null` (e `drill` anche) quando il numero non è calcolabile con i filtri scelti. F-141: anche lead,
 * appuntamenti, task scaduti, gare e anomalie di durata hanno il loro `drill` (metriche in `OPERATIONAL_METRICS`),
 * che si legge con `drilldownOperational`.
 */
export interface ExecutiveFilters {
  /** "YYYY-MM-DD", giorno di Roma, inclusivo. Senza date: ultimi 30 giorni. */
  from?: string | undefined
  to?: string | undefined
  agentId?: string | undefined
  /** Intero sottoalbero dell'utente indicato (lui compreso). */
  teamOf?: string | undefined
  /** Deve essere un EqualAreaManager, altrimenti 400. */
  areaManagerId?: string | undefined
  departmentId?: string | undefined
  operatorId?: string | undefined
  productId?: string | undefined
  status?: string | undefined
  contestId?: string | undefined
  /** "consumer" | "business" (segmento cliente della pratica). */
  customerType?: string | undefined
}

export interface ExecutiveDrill {
  metric: string
  key: string
}

export interface ExecutiveCell {
  count: number | null
  drill: ExecutiveDrill | null
}

export interface ExecutiveBucket {
  key: string
  label: string
  inserted: ExecutiveCell
  submitted: ExecutiveCell
  activated: ExecutiveCell
}

export interface ExecutiveRankRow {
  key: string
  label: string
  activated: ExecutiveCell
  inserted: ExecutiveCell
}

export interface ExecutiveDayRow {
  day: string
  inserted: ExecutiveCell
  activated: ExecutiveCell
}

export interface ExecutiveComparisonRow {
  measure: 'inserted' | 'submitted' | 'activated' | 'reversed'
  current: number
  previous: number
  delta: number
  /** Frazione (0.25 = +25%); null con base 0. */
  deltaPct: number | null
}

export interface ExecutiveStatusCell {
  status: string
  count: ExecutiveCell
}

export interface ExecutiveQualityRate {
  /** rejectRate, cancellationRate, integrationRate, activationRateVsSubmitted, activationRateOnContracts. */
  name: string
  /** Frazione 0..1 a 4 decimali; null se il denominatore è 0. */
  rate: number | null
  numerator: ExecutiveCell
  denominator: ExecutiveCell
}

export interface ExecutiveActiveContest {
  id: number
  name: string
  startDate: string
  endDate: string
  participants: number
  /** F-141 (REQ-037-003): apre l'elenco dei partecipanti (`drilldownOperational`). */
  drill: ExecutiveDrill
}

export interface ExecutiveReport {
  /** F-113: 'aggregate' se produzione, classifiche, andamento e confronto vengono dall'aggregato giornaliero. */
  dataSource?: 'live' | 'aggregate'
  /** F-113: quando e' stato calcolato l'ultimo giorno dell'aggregato letto (UTC). */
  computedAt?: string | null
  from: string
  to: string
  timeZone: string
  production: {
    inserted: ExecutiveCell
    submitted: ExecutiveCell
    activated: ExecutiveCell
    reversed: ExecutiveCell
    byDepartment: ExecutiveBucket[]
    byOperator: ExecutiveBucket[]
    byTeam: ExecutiveBucket[]
    byProduct: ExecutiveBucket[]
  }
  topAgents: ExecutiveRankRow[]
  topProducts: ExecutiveRankRow[]
  daily: ExecutiveDayRow[]
  comparison: {
    previousFrom: string
    previousTo: string
    metrics: ExecutiveComparisonRow[]
  }
  pipeline: { total: ExecutiveCell; byStatus: ExecutiveStatusCell[] }
  quality: ExecutiveQualityRate[]
  operational: {
    applicable: boolean
    newLeads: ExecutiveCell
    appointmentsCompleted: ExecutiveCell
    appointmentsNoShow: ExecutiveCell
    tasksOverdue: ExecutiveCell
    activeContests: ExecutiveActiveContest[]
  }
  backoffice: {
    backlog: { total: ExecutiveCell; byStatus: ExecutiveStatusCell[] }
    slaBreached: ExecutiveCell
    slaEvaluated: number
    exceptions: {
      total: ExecutiveCell
      byType: { type: string; count: ExecutiveCell }[]
    }
  }
  snapshot: { contractsInScope: number; migrated: number; migratedSnapshotOnly: number }
  limitations: string[]
}

export interface ExecutiveDrillItem {
  id: number
  referenceCode: string
  agentId: number | null
  agentName: string | null
  departmentId: number | null
  departmentName: string | null
  status: string
  createdAt: string
}

/** F-141 (REQ-037-003): le metriche del cockpit operativo; tutte le altre sono pratiche (`drilldown`). */
export const OPERATIONAL_METRICS = [
  'leads', 'appointments_completed', 'appointments_no_show', 'tasks_overdue', 'contest', 'duration_anomaly',
] as const

export function isOperationalMetric(metric: string): boolean {
  return (OPERATIONAL_METRICS as readonly string[]).includes(metric)
}

/** Una riga del drill-down operativo: lead, appuntamento, task, partecipante di una gara o pratica. */
export interface ExecutiveOperationalItem {
  id: number
  kind: 'lead' | 'appointment' | 'task' | 'participant' | 'contract'
  title: string
  date: string | null
  status: string | null
  /** Percorso del dettaglio nell'applicazione (es. `/leads/12`). */
  link: string
  userId: number | null
  userName: string | null
  /** Posizione in classifica (solo partecipanti a una gara). */
  position: number | null
}

export interface ExecutiveOperationalPage {
  metric: string
  key: string
  applicable: boolean
  total: number
  page: number
  pageSize: number
  items: ExecutiveOperationalItem[]
}

export interface ExecutiveDrillPage {
  metric: string
  key: string
  total: number
  page: number
  pageSize: number
  items: ExecutiveDrillItem[]
}

/**
 * F-082 (TASK-0336) — management by exception: anomalie dei KPI, baseline e confronto mese su mese per dimensione.
 * Nessun importo. `dataStatus` "insufficiente" NON significa "nessuna anomalia": mancano storico o campione.
 */
export type ExecutiveAnomalyKind =
  | 'kpi' | 'durata' | 'sla'
  | 'scostamento_operatore' | 'scarto_team' | 'costo_lead' | 'cash_flow'

export interface ExecutiveAnomaly {
  kind: ExecutiveAnomalyKind
  kpi: string
  kpiLabel: string
  /** all | department | operator | team | product | agent */
  dimension: string
  key: string
  label: string
  rules: string[]
  severity: 'alta' | 'media'
  direction: 'aumento' | 'calo'
  current: number
  numerator: number | null
  denominator: number | null
  baseline: { key: string; label: string; value: number | null } | null
  /** Percentuale (150 = +150%), non una frazione. */
  deviationPct: number | null
  zScore: number | null
  limit: number | null
  title: string
  /** Coppia da passare a `drilldown` (F-141: anche per le anomalie di durata, metrica `duration_anomaly`). */
  drill: ExecutiveDrill | null
}

export interface ExecutiveAnomalyParams {
  deviationPct?: number | undefined
  minSample?: number | undefined
  zScore?: number | undefined
  /** Percentuale (20 = 20%). */
  rejectRateMax?: number | undefined
  activatedMin?: number | undefined
  slaBreachedMax?: number | undefined
}

export interface ExecutiveAnomalies {
  from: string
  to: string
  thresholdsProvisional: boolean
  dataStatus: 'ok' | 'parziale' | 'insufficiente'
  dataStart: string | null
  note: string | null
  evaluated: number
  insufficient: number
  total: number
  anomalies: ExecutiveAnomaly[]
  limitations: string[]
}

export interface ExecutiveBaselineValue {
  key: string
  label: string
  from: string | null
  to: string | null
  value: number | null
  sample: number
  sufficient: boolean
  reason: 'copertura' | 'campione' | null
}

export interface ExecutiveKpiBaselines {
  kpi: string
  kpiLabel: string
  key: string
  label: string
  current: number | null
  numerator: number
  denominator: number | null
  status: 'ok' | 'insufficiente'
  baselines: ExecutiveBaselineValue[]
}

export interface ExecutiveBaselines {
  from: string
  to: string
  dimension: string
  minSample: number
  dataStart: string | null
  items: ExecutiveKpiBaselines[]
  limitations: string[]
}

export interface ExecutiveCompareCell {
  a: number
  b: number
  delta: number
  /** Frazione (0.25 = +25%); null con base 0. */
  deltaPct: number | null
  drillA?: { metric: string; key: string; from: string; to: string }
  drillB?: { metric: string; key: string; from: string; to: string }
}

export interface ExecutiveCompare {
  dimension: string
  a: { month: string; from: string; to: string }
  b: { month: string; from: string; to: string }
  measures: string[]
  totals: Record<string, ExecutiveCompareCell>
  rows: { key: string; label: string; measures: Record<string, ExecutiveCompareCell> }[]
  limitations: string[]
}

function qs(f: ExecutiveFilters, extra: Record<string, string | number | undefined> = {}): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries({ ...f, ...extra })) {
    if (v !== undefined && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// C-02: KPI commerciali estesi (flag report.kpi_estesi). Le chiavi economiche arrivano solo con la lettura amministrativa.
export interface KpiProjection {
  asOf: string
  soFar: number
  elapsedDays: number
  totalDays: number
  elapsedWorkingDays: number
  totalWorkingDays: number
  linear: number
  workingDays: number | null
}
export interface KpiRevenueStates { presumed: number; accrued: number; collected: number }
export interface KpiRevenueRow { key: string; label: string; states: KpiRevenueStates }
export interface ExecutiveKpiExtended {
  enabled: boolean
  money?: boolean
  period?: { from: string; to: string }
  conversion?: { name: string; numerator: number; denominator: number; rate: number | null }
  activationRates?: { name: string; numerator: number; denominator: number; rate: number | null }[]
  projection?: { activated: KpiProjection }
  revenue?: null | {
    total: KpiRevenueStates
    collectionRate: number | null
    byAgent: KpiRevenueRow[]
    byContract: KpiRevenueRow[]
    projection: { accrued: KpiProjection; collected: KpiProjection }
  }
  limitations?: string[]
}
// R3-18: vista unica di ricavi e provvigioni (flag report.ricavi_provvigioni), solo con la lettura amministrativa.
export interface RevenueCommissionRow {
  key: string
  label: string
  revenue: KpiRevenueStates
  commission: { presumed: number; accrued: number; paid: number }
}
export interface ExecutiveRevenueCommission {
  enabled: boolean
  money?: boolean
  period?: { from: string; to: string }
  total?: { revenue: KpiRevenueStates; commission: RevenueCommissionRow['commission'] }
  byAgent?: RevenueCommissionRow[]
  byContract?: RevenueCommissionRow[]
  limitations?: string[]
}
// R3-21: sezione economica del cockpit (flag report.cockpit_economico), solo con la lettura amministrativa.
export interface ExecutiveEconomic {
  enabled: boolean
  money?: boolean
  period?: { from: string; to: string }
  figuresMonth?: { from: string; to: string }
  revenue?: KpiRevenueStates
  commissions?: { presumed: number; accrued: number; paid: number; due: number }
  margin?: { amount: number; pct: number | null }
  costs?: number
  liquidity?: { available: boolean; bank: number | null; cash: number | null; free: number | null }
  receivables?: number
  payables?: number
  cashFlow?: { in30: number; out30: number; net: number }
  marketing?: { spend: number; leads: number; activations: number; cac: number | null; roi: number | null; costPerLead: number | null }
  goal?: { target: number; actual: number; pct: number } | null
  topProducts?: { key: string; label: string; practices: number; revenue: number; margin: number }[]
  anomalies?: ExecutiveAnomaly[]
  limitations?: string[]
}
export interface KpiCompareCell { a: number; b: number; delta: number; deltaPct: number | null }
export interface ExecutiveKpiCompare {
  enabled: boolean
  money?: boolean
  dimension?: string
  a?: { month: string }
  b?: { month: string }
  rows?: { key: string; label: string; inserted: KpiCompareCell; activated: KpiCompareCell; accrued: KpiCompareCell | null; collected: KpiCompareCell | null }[]
  limitations?: string[]
}

export const executiveApi = {
  get: (f: ExecutiveFilters = {}) => api<ExecutiveReport>(`/api/reports/executive${qs(f)}`),
  drilldown: (f: ExecutiveFilters, drill: ExecutiveDrill, page = 1, pageSize = 25) =>
    api<ExecutiveDrillPage>(
      `/api/reports/executive/drilldown${qs(f, { metric: drill.metric, key: drill.key, page, pageSize })}`,
    ),
  /** F-141 (REQ-037-003): righe dietro lead, appuntamenti, task scaduti, gare e anomalie di durata. */
  drilldownOperational: (f: ExecutiveFilters, drill: ExecutiveDrill, page = 1, pageSize = 25) =>
    api<ExecutiveOperationalPage>(
      `/api/reports/executive/drilldown-operational${qs(f, { metric: drill.metric, key: drill.key, page, pageSize })}`,
    ),
  /** F-082: anomalie dei KPI, con gli stessi filtri del cockpit. */
  anomalies: (f: ExecutiveFilters, p: ExecutiveAnomalyParams = {}) =>
    api<ExecutiveAnomalies>(`/api/reports/executive/anomalies${qs(f, { ...p })}`),
  baselines: (f: ExecutiveFilters, kpi?: string, dimension?: string) =>
    api<ExecutiveBaselines>(`/api/reports/executive/baselines${qs(f, { kpi, dimension })}`),
  /** Mesi "YYYY-MM"; delta = B meno A. */
  compare: (f: ExecutiveFilters, a: string, b: string, dimension: string) =>
    api<ExecutiveCompare>(`/api/reports/executive/compare${qs(f, { a, b, dimension })}`),
  economic: (f: ExecutiveFilters) => api<ExecutiveEconomic>(`/api/reports/executive/economico${qs(f)}`),
  revenueCommission: (f: ExecutiveFilters) =>
    api<ExecutiveRevenueCommission>(`/api/reports/executive/ricavi-provvigioni${qs(f)}`),
  kpiExtended: (f: ExecutiveFilters) => api<ExecutiveKpiExtended>(`/api/reports/executive/kpi-estesi${qs(f)}`),
  kpiCompare: (f: ExecutiveFilters, a: string, b: string, dimension: string) =>
    api<ExecutiveKpiCompare>(`/api/reports/executive/kpi-estesi/compare${qs(f, { a, b, dimension })}`),
}
